import { useMemo } from "react";
import { Table } from "antd";
import { ApartmentOutlined, TeamOutlined } from "@ant-design/icons";
import { fetchTeam } from "../../../services/customer360Service";
import {
  cardStyle, num, dateTime, chip, TONE,
  StatTile, TileGrid, PanelHeader, PanelSkeleton, PanelEmpty,
  PanelError, usePanelData,
} from "./shared";

/**
 * Who exists on the account, and who answers to whom.
 *
 * The hierarchy is not stored as a tree — it is a flat list of roles plus a
 * separate list of "this role reports to that one". The shape has to be rebuilt
 * here, which is the only real work on this tab.
 */

/**
 * Roles as a tree.
 *
 * Two things this guards against, because a self-referential table invites both:
 * a role whose parent was deleted would otherwise vanish from the tree entirely,
 * so anything without a resolvable parent is treated as a root; and a reporting
 * cycle would recurse forever, so each role is placed once and a node already
 * seen on the current path is not followed again.
 */
const buildTree = (roles = [], reports = []) => {
  const byId = new Map(roles.map((r) => [String(r.role_id ?? r.id), { ...r, children: [] }]));
  const parentOf = new Map(
    reports
      .filter((r) => r.parent_role_id != null)
      .map((r) => [String(r.role_id), String(r.parent_role_id)]),
  );

  const roots = [];
  byId.forEach((node, id) => {
    const parentId = parentOf.get(id);
    const parent = parentId && parentId !== id ? byId.get(parentId) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });

  /* A cycle among non-root roles would leave them unreachable from any root.
     Surfacing them at the top beats dropping them off the screen. */
  const reachable = new Set();
  const walk = (node, seen) => {
    const id = String(node.role_id ?? node.id);
    if (seen.has(id)) { node.children = []; return; }
    reachable.add(id);
    const next = new Set(seen).add(id);
    node.children.forEach((c) => walk(c, next));
  };
  roots.forEach((r) => walk(r, new Set()));
  byId.forEach((node, id) => { if (!reachable.has(id)) roots.push(node); });

  return roots;
};

const RoleNode = ({ node, depth = 0 }) => (
  <div style={{ paddingLeft: depth ? 18 : 0 }}>
    <div style={{
      display: "flex", alignItems: "center", gap: 9, padding: "8px 10px",
      borderRadius: 9, background: depth === 0 ? "#f5f3ff" : "transparent",
      border: depth === 0 ? "1px solid #ede9fe" : "1px solid transparent",
      marginBottom: 4,
    }}>
      {depth > 0 && (
        <span style={{ color: "#cbd5e1", fontSize: 12, lineHeight: 1, flexShrink: 0 }}>└</span>
      )}
      <span style={{ fontSize: 13, fontWeight: depth === 0 ? 700 : 500, color: "#0f172a" }}>
        {node.role_name || `Role #${node.role_id}`}
      </span>
      {Number(node.is_system) === 1 && chip("System", TONE.slate)}
    </div>
    {node.children?.map((c) => (
      <RoleNode key={c.role_id ?? c.id} node={c} depth={depth + 1} />
    ))}
  </div>
);

const TeamTab = ({ userId, active }) => {
  const p = usePanelData(fetchTeam, userId, active, { paged: false });

  const d = p.data?.data || {};
  const tree = useMemo(
    () => buildTree(d.roles || [], d.role_reports_to || []),
    [d.roles, d.role_reports_to],
  );

  if (p.loading && !p.data) return <PanelSkeleton tiles={2} />;
  if (p.error) return <PanelError error={p.error} onRetry={p.reload} />;

  const members = d.members || [];

  const memberColumns = [
    {
      title: "Name", dataIndex: "name", width: 170,
      render: (v) => <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 12.5 }}>{v || "—"}</span>,
    },
    {
      title: "Email", dataIndex: "email", width: 210,
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", wordBreak: "break-all" }}>{v || "—"}</span>,
    },
    {
      title: "Mobile", dataIndex: "mobile", width: 130,
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", fontVariantNumeric: "tabular-nums" }}>{v || "—"}</span>,
    },
    {
      title: "Status", key: "status", width: 140,
      /* Deleted outranks inactive. A member the customer removed is gone, not
         merely switched off, and showing that as "Inactive" reads as something
         an admin could toggle back on. */
      render: (_, r) => {
        if (Number(r.is_deleted) === 1) return chip("Deleted by user", TONE.red);
        if (Number(r.is_active) === 0) return chip("Inactive", TONE.slate);
        return chip("Active", TONE.green);
      },
    },
    {
      title: "Added", dataIndex: "created_at", width: 165,
      render: (v) => <span style={{ fontSize: 12.5, color: "#94a3b8", whiteSpace: "nowrap" }}>{dateTime(v)}</span>,
    },
  ];

  return (
    <>
      <TileGrid min={200}>
        <StatTile icon={<ApartmentOutlined />} label="Roles" value={num((d.roles || []).length)} color="#7c3aed" bg="#f5f3ff" />
        <StatTile icon={<TeamOutlined />} label="Team members" value={num(members.length)} color="#2563eb" bg="#eff6ff" />
      </TileGrid>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(260px,1fr) minmax(360px,2fr)", gap: 20, alignItems: "start" }}>
        <div style={cardStyle}>
          <PanelHeader
            icon={<ApartmentOutlined />}
            title="Role hierarchy"
            subtitle="Who reports to whom"
            iconBg="linear-gradient(135deg,#7c3aed,#8b5cf6)"
          />
          <div style={{ padding: "14px 18px 18px" }}>
            {tree.length === 0
              ? <PanelEmpty message="No roles defined on this account." />
              : tree.map((node) => <RoleNode key={node.role_id ?? node.id} node={node} />)}
          </div>
        </div>

        <div style={cardStyle}>
          <PanelHeader
            icon={<TeamOutlined />}
            title="Team members"
            subtitle="People with access to this account"
            iconBg="linear-gradient(135deg,#2563eb,#3b82f6)"
          />
          <Table
            columns={memberColumns}
            dataSource={members}
            rowKey={(r, i) => r.id ?? r.email ?? i}
            pagination={false}
            size="small"
            scroll={{ x: 815 }}
            locale={{ emptyText: <PanelEmpty message="No team members added yet." /> }}
          />
        </div>
      </div>
    </>
  );
};

export default TeamTab;
